// Shared by the `primary`/`secondary` aliases and the `green`/`blue` palettes so they can't drift.
const greenScale = {
  100: '#e0ffe0',
  200: '#baf5ba',
  300: '#93f593',
  400: '#51e051',
  500: '#1ec71e',
  600: '#1bb31b',
  700: '#189f18',
  800: '#158b15',
  900: '#0f630f',
};
const blueScale = {
  100: '#e0f0ff',
  200: '#c2e0ff',
  300: '#99ccff',
  400: '#5cadff',
  500: '#1f8fff',
  600: '#0975e0',
  700: '#075fb8',
  800: '#064a8f',
  900: '#043566',
};

export default {
  inherit: 'inherit',
  transparent: 'rgba(255,255,255,0)',
  white: '#fff',
  black: '#000',
  prose: {
    code: '#c7254e',
    preCode: '#33373d',
    preCodeBg: '#f5f5f5',
  },

  // Channel values live in CSS vars (packages/design/src/styles.css :root).
  // `<alpha-value>` enables opacity modifiers like `tw:bg-muted/50`.
  background: 'rgb(var(--color-background) / <alpha-value>)',
  foreground: 'rgb(var(--color-foreground) / <alpha-value>)',

  // Raised container chrome (Modal/Card/Sheet).
  card: {
    DEFAULT: 'rgb(var(--color-card) / <alpha-value>)',
    foreground: 'rgb(var(--color-card-foreground) / <alpha-value>)',
  },

  popover: {
    DEFAULT: 'rgb(var(--color-popover) / <alpha-value>)',
    foreground: 'rgb(var(--color-popover-foreground) / <alpha-value>)',
  },

  // Highlight color for active or focused elements
  accent: {
    DEFAULT: 'rgb(var(--color-accent) / <alpha-value>)',
    foreground: 'rgb(var(--color-accent-foreground) / <alpha-value>)',
  },

  border: 'rgb(var(--color-border) / <alpha-value>)',

  // `DEFAULT` is the input border; `background` its fill.
  // Not `card`: a form control re-skins independently of container chrome.
  input: {
    DEFAULT: 'rgb(var(--color-input) / <alpha-value>)',
    background: 'rgb(var(--color-input-background) / <alpha-value>)',
  },

  ring: 'rgb(var(--color-ring) / <alpha-value>)',

  // Focus-ring offset gap (the surface color the ring is inset against).
  'ring-offset': 'rgb(var(--color-ring-offset) / <alpha-value>)',

  // Categorical chart palette (brand-derived).
  chart: {
    1: 'rgb(var(--color-chart-1) / <alpha-value>)',
    2: 'rgb(var(--color-chart-2) / <alpha-value>)',
    3: 'rgb(var(--color-chart-3) / <alpha-value>)',
    4: 'rgb(var(--color-chart-4) / <alpha-value>)',
    5: 'rgb(var(--color-chart-5) / <alpha-value>)',
  },

  destructive: {
    DEFAULT: 'rgb(var(--color-destructive) / <alpha-value>)',
    foreground: 'rgb(var(--color-destructive-foreground) / <alpha-value>)',
  },
  success: {
    DEFAULT: 'rgb(var(--color-success) / <alpha-value>)',
    foreground: 'rgb(var(--color-success-foreground) / <alpha-value>)',
  },
  info: {
    DEFAULT: 'rgb(var(--color-info) / <alpha-value>)',
    foreground: 'rgb(var(--color-info-foreground) / <alpha-value>)',
  },

  muted: {
    DEFAULT: 'rgb(var(--color-muted) / <alpha-value>)',
    foreground: 'rgb(var(--color-muted-foreground) / <alpha-value>)',
  },

  // Skeleton fill; perceptible against `muted` and surfaces in both schemes.
  skeleton: 'rgb(var(--color-skeleton) / <alpha-value>)',

  // Table header tint (`muted` is too close to surfaces); dark override in styles.css.
  // Not a brand `Colors` slot, so brands don't re-skin it.
  'table-header': 'rgb(var(--color-table-header, 219 225 235) / <alpha-value>)',

  sidebar: {
    DEFAULT: 'rgb(var(--color-sidebar) / <alpha-value>)',
    foreground: 'rgb(var(--color-sidebar-foreground) / <alpha-value>)',
    primary: 'rgb(var(--color-sidebar-primary) / <alpha-value>)',
    'primary-foreground': 'rgb(var(--color-sidebar-primary-foreground) / <alpha-value>)',
    accent: 'rgb(var(--color-sidebar-accent) / <alpha-value>)',
    'accent-foreground': 'rgb(var(--color-sidebar-accent-foreground) / <alpha-value>)',
    border: 'rgb(var(--color-sidebar-border) / <alpha-value>)',
    ring: 'rgb(var(--color-sidebar-ring) / <alpha-value>)',
  },
  neutral: {
    50: '#f2f4f7',
    100: '#edf0f5',
    150: '#d9dde5',
    200: '#c5cbd6',
    300: '#a2aab8',
    400: '#7c8594',
    500: '#565c66',
    600: '#454952',
    700: '#33373d',
    800: '#222529',
    900: '#101114',
  },
  primary: {
    DEFAULT: 'rgb(var(--color-primary) / <alpha-value>)',
    foreground: 'rgb(var(--color-primary-foreground) / <alpha-value>)',
    ...greenScale,
  },
  secondary: {
    DEFAULT: 'rgb(var(--color-secondary) / <alpha-value>)',
    foreground: 'rgb(var(--color-secondary-foreground) / <alpha-value>)',
    ...blueScale,
  },
  error: {
    100: '#ffe0e6',
    200: '#ffc2cc',
    300: '#ff99aa',
    400: '#ff5c77',
    500: '#e00025',
    550: '#c40021',
    600: '#b8001f',
    700: '#a3001b',
    800: '#7a0014',
    900: '#52000e',
  },
  warning: {
    DEFAULT: 'rgb(var(--color-warning) / <alpha-value>)',
    foreground: 'rgb(var(--color-warning-foreground) / <alpha-value>)',
    100: '#fff3e0',
    200: '#ffe7c2',
    300: '#ffd699',
    400: '#ffbe5c',
    500: '#f59300',
    550: '#e38800',
    600: '#d17d00',
    700: '#a36200',
    800: '#7a4900',
    900: '#523100',
  },
  purple: {
    100: '#ebe0ff',
    200: '#d6c2ff',
    300: '#c9adff',
    400: '#ad85ff',
    500: '#925cff',
    600: '#6f36e0',
    700: '#5b2cb8',
    800: '#46228f',
    900: '#321866',
  },
  cyan: {
    100: '#e0f9ff',
    200: '#bef3ff',
    300: '#83ebff',
    400: '#00def7',
    500: '#00bbd1',
    600: '#00a0b2',
    700: '#007d8c',
    800: '#005d69',
    900: '#003f47',
  },
  teal: {
    100: '#cfffe6',
    200: '#96ffce',
    300: '#00faaf',
    400: '#00e6a1',
    500: '#00c287',
    600: '#00a673',
    700: '#008159',
    800: '#006141',
    900: '#00422b',
  },
  yellow: {
    100: '#FFFFCC',
    200: '#FFFF99',
    300: '#FFFF33',
    400: '#F5F500',
    500: '#E5E500',
    600: '#CCCC00',
    700: '#B2B200',
    800: '#999900',
    900: '#808000',
  },
  pink: {
    100: '#FDE8FB',
    200: '#FAD1F6',
    300: '#F7BBF2',
    400: '#F28CE8',
    500: '#ED5EDF',
    600: '#E619D1',
    700: '#B814A7',
    800: '#8A0F7D',
    900: '#5C0A54',
  },

  // Fixed brand-independent ramps; prefer over the `primary`/`secondary` ramp aliases.
  // `100..900` only; overrides Tailwind's same-named defaults.
  green: { ...greenScale },
  blue: { ...blueScale },
};
